import { IconType } from "react-icons";
import { 
  MdHome, 
  MdVerifiedUser, 
  MdUpgrade, 
  MdPerson, 
  MdPeopleAlt, 
  MdFingerprint, 
  MdFactCheck,
  MdPhoneAndroid,
  MdSupportAgent 
} from "react-icons/md";

export type UserRole = "Super Admin" | "Administrator" | "Firmware Specialist" | "Marketing";

export interface RolePermissions {
  // Page / Route access
  canAccessDashboard: boolean;
  canAccessDevices: boolean;
  canAccessDeviceQc: boolean;
  canAccessFirmware: boolean;
  canAccessUserManagement: boolean;
  canAccessProfile: boolean;
  canAccessMasterMac: boolean;
  canAccessIqcData: boolean;
  canAccessAppUsers: boolean;
  canAccessCustomerCare: boolean;
  canDeleteCustomerCare: boolean;

  // Device actions (Warranty Registration)
  canCreateDevice: boolean;
  canUploadDeviceExcel: boolean;
  canEditDevice: boolean;
  canDeleteDevice: boolean;
  canViewDevice: boolean;

  // Final QC actions
  canEditDeviceQc: boolean;
  canViewDeviceQc: boolean;

  // Firmware actions
  canUploadFirmware: boolean;
  canManageFirmware: boolean;
  canDeleteFirmware: boolean;

  // Master MAC actions
  canManageMasterMac: boolean;
  canDeleteMasterMac: boolean;

  // IQC actions
  canManageIqc: boolean;
  canDeleteIqc: boolean;

  // User management actions
  canCreateUser: boolean;
  canEditUser: boolean;
  canDeleteUser: boolean;
  canViewAllUsers: boolean;
}

export interface MenuItem {
  path: string;
  label: string;
  icon: IconType;
  permission?: keyof RolePermissions;
}

/**
 * Centralized Role-Based Permissions Configuration:
 * - Super Admin: Read, write, view, delete, update, edit for all pages and features.
 * - Administrator: Read, update, view, write for all pages and features (NO delete).
 * - Firmware Specialist: Read, write, update, view for ONLY Master Mac List, IQC List, Final QC List, Firmware (NO delete).
 * - Marketing: Read and view ONLY for Warranty Registration page.
 */
export const ROLE_PERMISSIONS: Record<UserRole, RolePermissions> = {
  "Super Admin": {
    canAccessDashboard: true,
    canAccessDevices: true,
    canAccessDeviceQc: true,
    canAccessFirmware: true,
    canAccessUserManagement: true,
    canAccessProfile: true,
    canAccessMasterMac: true,
    canAccessIqcData: true,
    canAccessAppUsers: true,
    canAccessCustomerCare: true,
    canDeleteCustomerCare: true,

    canCreateDevice: true,
    canUploadDeviceExcel: true,
    canEditDevice: true,
    canDeleteDevice: true,
    canViewDevice: true,

    canEditDeviceQc: true,
    canViewDeviceQc: true,

    canUploadFirmware: true,
    canManageFirmware: true,
    canDeleteFirmware: true,

    canManageMasterMac: true,
    canDeleteMasterMac: true,

    canManageIqc: true,
    canDeleteIqc: true,

    canCreateUser: true,
    canEditUser: true,
    canDeleteUser: true,
    canViewAllUsers: true,
  },
  Administrator: {
    canAccessDashboard: true,
    canAccessDevices: true,
    canAccessDeviceQc: true,
    canAccessFirmware: true,
    canAccessUserManagement: true,
    canAccessProfile: true,
    canAccessMasterMac: true,
    canAccessIqcData: true,
    canAccessAppUsers: true,
    canAccessCustomerCare: true,
    canDeleteCustomerCare: true,

    canCreateDevice: true,
    canUploadDeviceExcel: true,
    canEditDevice: true,
    canDeleteDevice: false,
    canViewDevice: true,

    canEditDeviceQc: true,
    canViewDeviceQc: true,

    canUploadFirmware: true,
    canManageFirmware: true,
    canDeleteFirmware: false,

    canManageMasterMac: true,
    canDeleteMasterMac: false,

    canManageIqc: true,
    canDeleteIqc: false,

    canCreateUser: true,
    canEditUser: true,
    canDeleteUser: false,
    canViewAllUsers: true,
  },
  "Firmware Specialist": {
    canAccessCustomerCare: false,
    canDeleteCustomerCare: false,
    canAccessDashboard: true,
    canAccessDevices: false,
    canAccessDeviceQc: true,
    canAccessFirmware: true,
    canAccessUserManagement: false,
    canAccessProfile: true,
    canAccessMasterMac: true,
    canAccessIqcData: true,
    canAccessAppUsers: false,

    canCreateDevice: false,
    canUploadDeviceExcel: false,
    canEditDevice: false,
    canDeleteDevice: false,
    canViewDevice: false,

    canEditDeviceQc: true,
    canViewDeviceQc: true,

    canUploadFirmware: true,
    canManageFirmware: true,
    canDeleteFirmware: false,

    canManageMasterMac: true,
    canDeleteMasterMac: false,

    canManageIqc: true,
    canDeleteIqc: false,

    canCreateUser: false,
    canEditUser: false,
    canDeleteUser: false,
    canViewAllUsers: false,
  },
  Marketing: {
    canAccessCustomerCare: true,
    canDeleteCustomerCare: false,
    canAccessDashboard: true,
    canAccessDevices: true,
    canAccessDeviceQc: false,
    canAccessFirmware: false,
    canAccessUserManagement: false,
    canAccessProfile: true,
    canAccessMasterMac: false,
    canAccessIqcData: false,
    canAccessAppUsers: false,

    canCreateDevice: false,
    canUploadDeviceExcel: false,
    canEditDevice: false,
    canDeleteDevice: false,
    canViewDevice: true,

    canEditDeviceQc: false,
    canViewDeviceQc: false,

    canUploadFirmware: false,
    canManageFirmware: false,
    canDeleteFirmware: false,

    canManageMasterMac: false,
    canDeleteMasterMac: false,

    canManageIqc: false,
    canDeleteIqc: false,

    canCreateUser: false,
    canEditUser: false,
    canDeleteUser: false,
    canViewAllUsers: false,
  },
};

/**
 * Normalizes input role string to one of the 4 supported UserRole types
 */
export const normalizeRole = (rawRole?: string | null): UserRole => {
  if (!rawRole) return "Administrator";
  const lower = rawRole.toString().trim().toLowerCase();

  if (lower === "super admin" || lower === "superadmin" || lower.includes("super")) {
    return "Super Admin";
  }
  if (lower.includes("marketing")) {
    return "Marketing";
  }
  if (lower.includes("firmware")) {
    return "Firmware Specialist";
  }
  if (lower.includes("admin")) {
    return "Administrator";
  }

  // Exact matches
  if (rawRole === "Super Admin") return "Super Admin";
  if (rawRole === "Firmware Specialist") return "Firmware Specialist";
  if (rawRole === "Marketing") return "Marketing";
  return "Administrator";
};

/**
 * Retrieves the currently logged-in user's role from localStorage / token
 */
export const getCurrentUserRole = (): UserRole => {
  // 1. Check explicit localStorage entry
  const storedRole = localStorage.getItem("userRole");
  if (storedRole) {
    return normalizeRole(storedRole);
  }

  // 2. Decode from JWT token payload safely if userRole key is absent
  const token = localStorage.getItem("token");
  if (token) {
    try {
      const parts = token.split(".");
      if (parts.length === 3) {
        const payloadJson = atob(parts[1]);
        const payload = JSON.parse(payloadJson);
        const roleInToken = payload.role || payload.org;
        if (roleInToken) {
          const normalized = normalizeRole(roleInToken);
          localStorage.setItem("userRole", normalized);
          return normalized;
        }
      }
    } catch (e) {
      console.warn("Unable to decode JWT for role extraction:", e);
    }
  }

  return "Administrator";
};

/**
 * Check if the given or current user role has a specific permission
 */
export const hasPermission = (
  permission: keyof RolePermissions,
  role?: string | null
): boolean => {
  const currentRole = role ? normalizeRole(role) : getCurrentUserRole();
  const permissions = ROLE_PERMISSIONS[currentRole] || ROLE_PERMISSIONS["Administrator"];
  return Boolean(permissions[permission]);
};

/**
 * Get dynamic sidebar navigation items based strictly on user role:
 * - Super Admin: Dashboard • Master MAC • IQC List • Final QC List • Warranty Registration • Firmware • App Signups • Users • Profile
 * - Administrator: Dashboard • Master MAC • IQC List • Final QC List • Warranty Registration • Firmware • App Signups • Users • Profile
 * - Firmware Specialist: Dashboard • Master MAC • IQC List • Final QC List • Firmware • Profile
 * - Marketing: Dashboard • Warranty Registration • Profile
 */
export const getSidebarMenuItems = (role?: string | null): MenuItem[] => {
  const userRole = role ? normalizeRole(role) : getCurrentUserRole();

  switch (userRole) {
    case "Super Admin":
      return [
        { path: "home", label: "Dashboard", icon: MdHome, permission: "canAccessDashboard" },
        { path: "master-mac", label: "Master Mac ID List", icon: MdFingerprint, permission: "canAccessMasterMac" },
        { path: "iqc-data", label: "IQC List", icon: MdFactCheck, permission: "canAccessIqcData" },
        { path: "device-qc", label: "Final QC List", icon: MdFactCheck, permission: "canAccessDeviceQc" },
        { path: "devices", label: "Warranty Registration", icon: MdVerifiedUser, permission: "canAccessDevices" },
        { path: "firmwareupdate", label: "Firmware", icon: MdUpgrade, permission: "canAccessFirmware" },
        { path: "app-users", label: "App Signups", icon: MdPhoneAndroid, permission: "canAccessAppUsers" },
        { path: "customer-care", label: "Customer Queries", icon: MdSupportAgent, permission: "canAccessCustomerCare" },
        { path: "users", label: "Users", icon: MdPeopleAlt, permission: "canAccessUserManagement" },
        { path: "profile", label: "Profile", icon: MdPerson, permission: "canAccessProfile" },
      ];

    case "Firmware Specialist":
      return [
        { path: "home", label: "Dashboard", icon: MdHome, permission: "canAccessDashboard" },
        { path: "master-mac", label: "Master Mac ID List", icon: MdFingerprint, permission: "canAccessMasterMac" },
        { path: "iqc-data", label: "IQC List", icon: MdFactCheck, permission: "canAccessIqcData" },
        { path: "device-qc", label: "Final QC List", icon: MdFactCheck, permission: "canAccessDeviceQc" },
        { path: "firmwareupdate", label: "Firmware", icon: MdUpgrade, permission: "canAccessFirmware" },
        { path: "profile", label: "Profile", icon: MdPerson, permission: "canAccessProfile" },
      ];

    case "Marketing":
      return [
        { path: "home", label: "Dashboard", icon: MdHome, permission: "canAccessDashboard" },
        { path: "devices", label: "Warranty Registration", icon: MdVerifiedUser, permission: "canAccessDevices" },
        { path: "customer-care", label: "Customer Queries", icon: MdSupportAgent, permission: "canAccessCustomerCare" },
        { path: "profile", label: "Profile", icon: MdPerson, permission: "canAccessProfile" },
      ];

    /* OLD_MARKETING_FALLBACK
      return [
        { path: "home", label: "Dashboard", icon: MdHome, permission: "canAccessDashboard" },
        { path: "devices", label: "Warranty Registration", icon: MdVerifiedUser, permission: "canAccessDevices" },
        { path: "profile", label: "Profile", icon: MdPerson, permission: "canAccessProfile" },
      ];

    */
    case "Administrator":
    default:
      return [
        { path: "home", label: "Dashboard", icon: MdHome, permission: "canAccessDashboard" },
        { path: "master-mac", label: "Master Mac ID List", icon: MdFingerprint, permission: "canAccessMasterMac" },
        { path: "iqc-data", label: "IQC List", icon: MdFactCheck, permission: "canAccessIqcData" },
        { path: "device-qc", label: "Final QC List", icon: MdFactCheck, permission: "canAccessDeviceQc" },
        { path: "devices", label: "Warranty Registration", icon: MdVerifiedUser, permission: "canAccessDevices" },
        { path: "firmwareupdate", label: "Firmware", icon: MdUpgrade, permission: "canAccessFirmware" },
        { path: "app-users", label: "App Signups", icon: MdPhoneAndroid, permission: "canAccessAppUsers" },
        { path: "customer-care", label: "Customer Queries", icon: MdSupportAgent, permission: "canAccessCustomerCare" },
        { path: "users", label: "Users", icon: MdPeopleAlt, permission: "canAccessUserManagement" },
        { path: "profile", label: "Profile", icon: MdPerson, permission: "canAccessProfile" },
      ];
  }
};
